import React, { useState, useEffect } from "react";
import axios from "axios";
import { sha256 } from "crypto-hash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import getIconForSocialNetwork from "../../../../helpers/getIconForSocialNetwork";
import haveHttp from "../../../../helpers/haveHttp";
import areObjectsEqual from "../../../../utils/areObjectsEqual";
import { useError } from "../../../../context/ErrorContextProvider";
import icon_image from "../../../../icons/icon-image.svg";
import fr_black_white from "../../../../assets/images/logo/fr_black_white.svg";
import fr_green from "../../../../assets/images/logo/fr_green.svg";
import fr_yellow from "../../../../assets/images/logo/fr_yellow.svg";
import fr_purple from "../../../../assets/images/logo/fr_purple.svg";
import fr_violet from "../../../../assets/images/logo/fr_violet.svg";
import { maxFileSizeValidation } from "../../../../utils/common";

const LOGO_LIST = [
  { image: fr_yellow, name: "Logo 1", type: "yellow" },
  { image: fr_purple, name: "Logo 2", type: "purple" },
  { image: fr_violet, name: "Logo 3", type: "violet" },
  { image: fr_green, name: "Logo 4", type: "green" },
  { image: fr_black_white, name: "Logo 5", type: "black_white" },
];

const IMAGE_SIZE_CONSTRAINTS = { min: 50, max: 70 };

const fileAPI = {
  upload: (file, encryptedName) => {
    const formData = new FormData();
    formData.append("file", file, encryptedName);
    return axios.post(`/api/file/upload`, formData);
  },
};

const Form = ({ formData, handleSubmit, imageBuffer }) => {
  const { showError } = useError();

  const [form, setForm] = useState(formData);
  const [copyData, setCopyData] = useState();
  const hasUnsavedChanges = areObjectsEqual(copyData, form);

  const [httpValidation, setHttpValidation] = useState({
    link1: true,
    link2: true,
    link3: true,
  });

  const [icons, setIcons] = useState({
    ct_icon_1: { status: false, file: null, encryptedName: "", preview: null },
    ct_icon_2: { status: false, file: null, encryptedName: "", preview: null },
    ct_icon_3: { status: false, file: null, encryptedName: "", preview: null },
    ct_icon_4: { status: false, file: null, encryptedName: "", preview: null },
    ct_icon_5: { status: false, file: null, encryptedName: "", preview: null },
    ct_icon_6: { status: false, file: null, encryptedName: "", preview: null },
    ct_icon_nft: { status: false, file: null, encryptedName: "", preview: null },
    ct_graphic_nft: { status: false, file: null, encryptedName: "", preview: null },
  });

  const [isListOpen, setList] = useState(false);
  const [logo, setLogo] = useState(fr_black_white);
  const [logoHeader, setHeaderLogo] = useState("Select a logo");
  const [showLogo, setShowLogo] = useState(false);
  const [radius, setRadius] = useState("0.5rem");

  const [socialLinks, setSocialLinks] = useState({
    ct_social_link_1: { url: form.ct_social_link_1, icon: icon_image },
    ct_social_link_2: { url: form.ct_social_link_2, icon: icon_image },
    ct_social_link_3: { url: form.ct_social_link_3, icon: icon_image },
    ct_social_link_4: { url: form.ct_social_link_4, icon: icon_image },
  });

  const toggleList = () => {
    setList(!isListOpen);
    setRadius(radius === "0.5rem" ? "0.5rem 0.5rem 0 0" : "0.5rem");
  };

  const changeLogo = (logo) => {
    if (logo) {
      setLogo(logo.image);
      setHeaderLogo(logo.name);
      setShowLogo(true);
      setForm((prev) => ({ ...prev, ct_main_logo: logo.type }));
      setRadius("0.5rem");
    }
  };

  useEffect(() => {
    setForm({ ...formData, formData });
    setCopyData({ ...formData, formData });
  }, [formData]);

  useEffect(() => {
    if (form.ct_main_logo) {
      const selectedLogo = LOGO_LIST.find((logo) => logo.type === form.ct_main_logo);
      if (selectedLogo) changeLogo(selectedLogo);
    } else {
      setHeaderLogo("Select a logo");
      setShowLogo(false);
    }
  }, [form.ct_main_logo]);

  const handleChange = (e) => {
    const newLink = e.target.value;
    const linkKey = e.target.name;
    const updatedLinks = { ...socialLinks };

    const linkMap = {
      ct_social_link_1: "link1",
      ct_social_link_2: "link2",
      ct_social_link_3: "link3",
    };

    const isValid = !newLink || haveHttp(newLink);
    if (linkMap[linkKey]) {
      setHttpValidation((prev) => ({ ...prev, [linkMap[linkKey]]: isValid }));
    }

    if (newLink) {
      updatedLinks[linkKey] = {
        url: newLink,
        icon: getIconForSocialNetwork(newLink),
      };
    } else {
      updatedLinks[linkKey] = { url: "", icon: icon_image };
    }

    setSocialLinks(updatedLinks);
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const extensionFile = file.name.split(".").pop();
    const date = Date.now();
    const fieldName = e.target.name;

    const sizeError = maxFileSizeValidation(file);
    if (sizeError) {
      showError(sizeError);
      return;
    }

    const image = new Image();
    image.onload = async () => {
      const { width, height } = image;
      const { min, max } = IMAGE_SIZE_CONSTRAINTS;

      if (width < min || width > max || height < min || height > max) {
        showError(
          `The image must have a size between ${min}x${min} and ${max}x${max}. Current size: ${width}x${height}`
        );
        return;
      }

      try {
        const sha = await sha256(`${date}`);
        const encryptedName = `${sha}.${extensionFile}`;

        setForm((prev) => ({ ...prev, [fieldName]: encryptedName }));
        setIcons((prev) => ({
          ...prev,
          [fieldName]: {
            status: true,
            file,
            encryptedName,
            preview: URL.createObjectURL(file),
          },
        }));
      } catch (error) {
        console.error("Error processing image:", error);
        showError("Failed to process image");
      }
    };

    image.src = URL.createObjectURL(file);
  };

  const renderIconImage = (fieldName) => {
    const iconData = icons[fieldName];
    const formValue = form[fieldName];

    if (!formValue) {
      return <img src={icon_image} className="custom-choose-file-small" alt="default" />;
    }

    if (iconData.status && iconData.preview) {
      return <img src={iconData.preview} className="custom-choose-file-small" alt="preview" />;
    }

    try {
      const buffer = imageBuffer[fieldName];
      if (!buffer?.image?.data) {
        return <img src={icon_image} className="custom-choose-file-small" alt="default" />;
      }

      const uint8Array = new Uint8Array(buffer.image.data);
      let binaryString = "";
      for (let i = 0; i < uint8Array.length; i++) {
        binaryString += String.fromCharCode(uint8Array[i]);
      }

      return (
        <img
          src={`data:${buffer.type};base64,${btoa(binaryString)}`}
          className="custom-choose-file-small"
          alt={fieldName}
        />
      );
    } catch (error) {
      console.error(`Error processing ${fieldName} image:`, error);
      return <img src={icon_image} className="custom-choose-file-small" alt="default" />;
    }
  };

  const handleClick = async () => {
    const { link1, link2, link3 } = httpValidation;
    if (!link1 || !link2 || !link3) return;

    try {
      for (const fieldName in icons) {
        const iconData = icons[fieldName];
        if (iconData.file && iconData.encryptedName) {
          await fileAPI.upload(iconData.file, iconData.encryptedName);
        }
      }

      form.ct_check_main_page_updated = true;
      handleSubmit(form);
      setCopyData(form);
    } catch (error) {
      console.error("Error uploading images:", error);
      showError("Failed to upload images");
    }
  };

  return (
    <>
      <div className="header">
        <h1 className="main-primary-text">Header</h1>

        <div className="nav-bar">
          <h2 className="main-secondary-text">Nav Bar</h2>
          <h3 className="main-secondary-text">Brand</h3>
          <div className="logo-name__dropdown-wrapper" onClick={toggleList}>
            <div className="logo-name__dropdown-top" style={{ borderRadius: radius }}>
              <div className="logo-name__dropdown-header">
                <div className="logo-choice-block">
                  {showLogo && <img src={logo} className="header-logo" alt="logo" />}
                  <span className="logo-name">{logoHeader}</span>
                </div>
              </div>
              <div className="chevron-down">
                <FontAwesomeIcon icon="fa-solid fa-chevron-down" size="xl" />
              </div>
            </div>
            <div className={`logo-name__dropdown-bottom ${isListOpen ? "d-block" : "d-none"}`}>
              <ul className="logo-name__lists">
                {LOGO_LIST.map((logo, index) => (
                  <li key={index} className="logo-name__list" onClick={() => changeLogo(logo)}>
                    <div className="logo-choice-block">
                      <img src={logo.image} className="list-logo" alt={logo.name} />
                      <span className="list-logo-name">{logo.name}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="mb-5">
          {/* <h2 className="main-secondary-text">Nav Menu</h2>*/}
          <div>
            {false && (
              <>
                {/******* section 1 *******/}
                <div className="row col-sm-12 d-flex image-row">
                  {/****************************/}
                  {/********** Icon 1 **********/}
                  {/****************************/}
                  <div className="row col-sm-3">
                    <div className="form-labels">Home</div>
                    <div className="col-sm-5 d-flex justify-content-start">
                      <div className="form-labels">
                        {renderIconImage("ct_icon_1")}
                      </div>
                    </div>

                    {/*btn and description*/}
                    <div className="col-sm-7 text_little_description">
                      <span className="d-block">- Size 70x70px</span>
                      <span>-.svg format</span>
                      <br />
                      <label className="btn btn-upload">
                        Upload
                        <input
                          type="file"
                          name="ct_icon_1"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>
                  {/****************************/}
                  {/******** end Icon 1 ********/}
                  {/****************************/}

                  {/****************************/}
                  {/********** Icon 2 **********/}
                  {/****************************/}
                  <div className="row col-sm-3">
                    <div className="form-labels">Top</div>
                    <div className="col-sm-5 d-flex justify-content-start">
                      <div className="form-labels">
                        {renderIconImage("ct_icon_2")}
                      </div>
                    </div>

                    {/*btn and description*/}
                    <div className="col-sm-7 text_little_description">
                      <span className="d-block">- Size 70x70px</span>
                      <span>-.svg format</span>

                      <br />
                      <label className="btn btn-upload">
                        Upload
                        <input
                          type="file"
                          name="ct_icon_2"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>
                  {/****************************/}
                  {/******** end Icon 2 ********/}
                  {/****************************/}

                  {/****************************/}
                  {/********** Icon 3 **********/}
                  {/****************************/}
                  <div className="row col-sm-3">
                    <div className="form-labels">My</div>
                    <div className="col-sm-5 d-flex justify-content-start">
                      <div className="form-labels">
                        {renderIconImage("ct_icon_3")}
                      </div>
                    </div>

                    {/*btn and description*/}
                    <div className="col-sm-7 text_little_description">
                      <span className="d-block">- Size 70x70px</span>
                      <span>-.svg format</span>

                      <br />
                      <label className="btn btn-upload">
                        Upload
                        <input
                          type="file"
                          name="ct_icon_3"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>
                  {/****************************/}
                  {/******** end Icon 3 ********/}
                  {/****************************/}

                  {/****************************/}
                  {/********** Icon 4 **********/}
                  {/****************************/}
                  <div className="row col-sm-3">
                    <div className="form-labels">About</div>
                    <div className="col-sm-5 d-flex justify-content-start">
                      <div className="form-labels">
                        {renderIconImage("ct_icon_4")}
                      </div>
                    </div>

                    {/*btn and description*/}
                    <div className="col-sm-7 text_little_description">
                      <span className="d-block">- Size 70x70px</span>
                      <span>-.svg format</span>

                      <br />
                      <label className="btn btn-upload">
                        Upload
                        <input
                          type="file"
                          name="ct_icon_4"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>
                  {/****************************/}
                  {/******** end Icon 4 ********/}
                  {/****************************/}
                </div>
                {/******* end section 1 *******/}

                {/******* section 2 *******/}
                <div className="row col-sm-12 d-flex image-row mt-2">
                  {/****************************/}
                  {/********** Icon 5 **********/}
                  {/****************************/}
                  <div className="row col-sm-3">
                    <div className="form-labels">Mint</div>
                    <div className="col-sm-5 d-flex justify-content-start">
                      <div className="form-labels">
                        {renderIconImage("ct_icon_5")}
                      </div>
                    </div>

                    {/*btn and description*/}
                    <div className="col-sm-7 text_little_description">
                      <span className="d-block">- Size 70x70px</span>
                      <span>-.svg format</span>

                      <br />
                      <label className="btn btn-upload">
                        Upload
                        <input
                          type="file"
                          name="ct_icon_5"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>

                  {/****************************/}
                  {/******** end Icon 5 ********/}
                  {/****************************/}

                  {/****************************/}
                  {/********** Icon 6 **********/}
                  {/****************************/}
                  <div className="row col-sm-3">
                    <div className="form-labels">Buy</div>
                    <div className="col-sm-5 d-flex justify-content-start">
                      <div className="form-labels">
                        {renderIconImage("ct_icon_6")}
                      </div>
                    </div>

                    {/*btn and description*/}
                    <div className="col-sm-6 text_little_description">
                      <span className="d-block">- Size 70x70px</span>
                      <span>-.svg format</span>

                      <br />
                      <label className="btn btn-upload">
                        Upload
                        <input
                          type="file"
                          name="ct_icon_6"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>

                  {/****************************/}
                  {/******** end Icon 6 ********/}
                  {/****************************/}
                </div>

                {/******* end section 2 *******/}

                {/*Separation line */}
                <div className="line-hr-big-white mt-2 mb-3"></div>

                <div className="row col-sm-12 image-row mt-4">


                  <div className="row col-sm-3">
                    <div className="fw-bold title-secondary-normal mb-2">
                      Top Supporter
                    </div>
                    <div className="form-labels">Icon NFT</div>
                    <div className="col-sm-5 d-flex justify-content-start">
                      {renderIconImage("ct_icon_nft")}
                    </div>


                    <div className="col-sm-7 text_little_description">
                      <span className="d-block">- Size 70x70px</span>
                      <span>-.svg format</span>
                      <br />

                      <label className="btn btn-upload">
                        Upload
                        <input
                          type="file"
                          name="ct_icon_nft"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>

                  {/****************************/}
                  {/********* Graphic Nft ******/}
                  {/****************************/}
                  <div className="row col-sm-3">
                    <div className="fw-bold title-secondary-normal mb-2">
                      We Need You
                    </div>
                    <div className="form-labels">Graphic NFT</div>
                    <div className="col-sm-5 d-flex justify-content-start">
                      {renderIconImage("ct_graphic_nft")}
                    </div>

                    {/*btn and description*/}
                    <div className="col-sm-7 text_little_description">
                      <span className="d-block">- Size 70x70px</span>
                      <span>-svg.format</span>
                      <br />
                      <label className="btn btn-upload">
                        Upload
                        <input
                          type="file"
                          name="ct_graphic_nft"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </label>
                    </div>
                  </div>
                  <div className="row col-sm-3">&nbsp;</div>
                </div> </>)}
          </div>
        </div>
      </div>
      <div className="footer">
        <h1 className="main-primary-text">Footer</h1>
        <h2 className="main-secondary-text">Social</h2>
        <div className="row col-sm-12 d-flex justify-content-between">
          <div className="d-flex justify-content-between">
            <div className="row col-sm-10">
              <label htmlFor="ct_social_link_1" className="form-labels">
                Link 1
              </label>
              <div className="col-sm-12">
                <input
                  type="text"
                  name="ct_social_link_1"
                  id="ct_social_link_1"
                  placeholder="https://facebook.com"
                  onChange={handleChange}
                  className="form-control mt-2 inputs"
                  value={form.ct_social_link_1 || ""}
                />
              </div>
              {!httpValidation.link1 && (
                <span className="form-text text-danger">
                  The url needs to have https at the beginning
                </span>
              )}
            </div>
            <div className="row col-sm-3 d-flex justify-content-center">
              <div className="col-sm-6 d-flex justify-content-center align-items-center">
                {getIconForSocialNetwork(form.ct_social_link_1)}
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between mt-4">
            <div className="row col-sm-10">
              <label htmlFor="ct_social_link_2" className="form-labels">
                Link 2
              </label>
              <div className="col-sm-12">
                <input
                  type="text"
                  name="ct_social_link_2"
                  id="ct_social_link_2"
                  placeholder="https://instagram.com"
                  onChange={handleChange}
                  className="form-control mt-2 inputs"
                  value={form.ct_social_link_2 || ""}
                />
              </div>
              {!httpValidation.link2 && (
                <span className="form-text text-danger">
                  The url needs to have https at the beginning
                </span>
              )}
            </div>
            <div className="row col-sm-3 d-flex justify-content-center">
              <div className="col-sm-6 d-flex justify-content-center align-items-center">
                {getIconForSocialNetwork(form.ct_social_link_2)}
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between mt-4">
            <div className="row col-sm-10">
              <label htmlFor="ct_social_link_3" className="form-labels">
                Link 3
              </label>
              <div className="col-sm-12">
                <input
                  type="text"
                  name="ct_social_link_3"
                  id="ct_social_link_3"
                  placeholder="https://twitter.com"
                  onChange={handleChange}
                  className="form-control mt-2 inputs"
                  value={form.ct_social_link_3 || ""}
                />
              </div>
              {!httpValidation.link3 && (
                <span className="form-text text-danger">
                  The url needs to have https at the beginning
                </span>
              )}
            </div>
            <div className="row col-sm-3 d-flex justify-content-center">
              <div className="col-sm-6 d-flex justify-content-center align-items-center">
                {getIconForSocialNetwork(form.ct_social_link_3)}
              </div>
            </div>
          </div>
          <div className="pt-4">
            <input
              type="submit"
              value="Save Changes"
              onClick={handleClick}
              className="btn bg-orange form-btn"
              disabled={hasUnsavedChanges}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Form;

